import useSavingCalculations from "@portal/hooks/robot/useSavingCalculations";
import Typography from "@portal/components/atoms/v2/typography";
import { useEffect, useState } from "react";
import { MapNumOfMonthsToIndex } from "./helpers";
import { InputOptions, ButtonOptions } from "./components";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { ISavingAgreementData } from "@portal/types/robot/ISavingAgreementData";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import { IsValidMonthlyAmount } from "@portal/helpers";
import { RobotFooterComponent } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";

import { RiskLevel } from "@portal/types/robot/IRobotEnums";
import { UserMessageComponent } from "../components/message/UserMessageComponent";
import { useRouter } from "next/navigation";
import { RadioGroup } from "@headlessui/react";
import { AgreementGraph } from "./components/agreementGraph";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import RadioComponent from "@portal/components/atoms/v2/radioComponent";
import { MainBox } from "../components/mainBox";

const MINIMUM_ONE_TIME_DEPOSIT = 250;

/**
 * Component for the details of the saving agreement.
 * This component wraps up many components, user inputs and graphics.
 * @returns a page to show a graph of expected saving results.
 */
export const AgreementDetailsPage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const options = [texts.PAGE_13_A, texts.PAGE_13_B, texts.PAGE_13_C, texts.PAGE_13_D];

  const { request } = useAppSelector(state => state.persisted.savingCalculations);
  const numOfMonthsIndex = MapNumOfMonthsToIndex(request.numOfMonthsStartupAmount);
  const [selectedOption, setSelectedOption] = useState<number>(numOfMonthsIndex);

  const riskLevel = useAppSelector(state => state.persisted.savingProposal.savingAgreementData.riskLevel);

  useSavingCalculations(request, dispatch);

  const [selectedRiskLevel, setSelectedRiskLevel] = useState<RiskLevel>(riskLevel || RiskLevel.Unknown);

  const isValidMonthlyAmount = IsValidMonthlyAmount(request.monthlyAmount);
  const isValidOneTimeDeposit = IsValidOneTimeDeposit(request);
  const isButtonDisabled = !IsValidRequest(isValidMonthlyAmount, isValidOneTimeDeposit, request);
  const { availableLiquidity, savingsToday } = useAppSelector(state => state.persisted.riskLevel.request);
  const [notEnoughLiquidity, setNotEnoughLiquidity] = useState(false);
  const [notEnoughSavings, setNotEnoughSavings] = useState(false);
  const [aboveMaxAmount, setAboveMaxAmount] = useState(false);

  useEffect(() => {
    if (riskLevel && selectedRiskLevel.toString() === RiskLevel.Unknown.toString()) {
      setSelectedRiskLevel(riskLevel);
    }
  }, [riskLevel, selectedRiskLevel]);

  return (
    <>
      <MainBox>
        <TextLine />
        <div className="flex mb-2.5">
          <Typography variant="Body">{texts.PAGE_13_SUBHEADER}</Typography>
        </div>
        <InputOptions
          request={request}
          isValidMonthlyAmount={isValidMonthlyAmount}
          isValidOneTimeDeposit={isValidOneTimeDeposit}
          isDefaultLanguage={isDefaultLanguage}
          dispatch={dispatch}
          availableLiquidity={availableLiquidity}
          setNotEnoughLiquidity={setNotEnoughLiquidity}
          savingsToday={savingsToday}
          setNotEnoughSavings={setNotEnoughSavings}
          setAboveMaxAmount={setAboveMaxAmount}
        />
        <ButtonOptions
          options={options}
          selectedItem={selectedOption}
          header={texts.PAGE_13_START_AMOUNT_SPREAD}
          setSelectedItem={setSelectedOption}
          dispatch={dispatch}
        />
        <AgreementGraph riskLevel={selectedRiskLevel} isDefaultLanguage={isDefaultLanguage} />
        <div className="mt-5 mb-5 sm:space-y-2">
          <Typography variant="Body" className="sm:text-center">
            {texts.PAGE_13_TEST_WITH_RISK_PROFILE_LABEL}
          </Typography>
          <RadioGroup
            value={selectedRiskLevel}
            onChange={setSelectedRiskLevel}
            className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:justify-between ml-2"
          >
            <RadioComponent
              id="riskLevelCareful"
              value={RiskLevel[RiskLevel.Careful]}
              text={texts.PAGE_13_TEST_WITH_RISK_CAREFUL_LABEL}
              large
            />
            <RadioComponent
              id="riskLevelBalanced"
              value={RiskLevel[RiskLevel.Balanced]}
              text={texts.PAGE_13_TEST_WITH_RISK_BALANCED_LABEL}
              large
            />
            <RadioComponent
              id="riskLevelOffensive"
              value={RiskLevel[RiskLevel.Offensive]}
              text={texts.PAGE_13_TEST_WITH_RISK_OFFENSIVE_LABEL}
              large
            />
            <RadioComponent
              id="riskLevelExtraOffensive"
              value={RiskLevel[RiskLevel.ExtraOffensive]}
              text={texts.PAGE_13_TEST_WITH_RISK_EXTRA_OFFENSIVE_LABEL}
              large
            />
          </RadioGroup>
        </div>
        {/* <TotalAmount calculation={calculation} isDefaultLanguage={isDefaultLanguage} /> */}
        <InfoBoxComponent
          header={texts.PAGE_13_REQUIREMENTS_HEADER}
          texts={[
            texts.PAGE_13_TEXT_A,
            texts.PAGE_13_TEXT_B,
            // texts.PAGE_13_TEXT_C,
            // texts.PAGE_13_TEXT_D,
            texts.PAGE_13_TEXT_E,
            texts.PAGE_13_TEXT_F,
            texts.PAGE_13_TEXT_G,
            texts.PAGE_13_TEXT_H,
          ]}
        />
        {/*
        Slider brukte styled components må evt lage ny om denne skal inn igjen
        <SliderComponent currentValue={request.numOfYears} text={texts.PAGE_13_NUM_YEARS} /> */}
        <UserMessageComponent
          userMessage={texts.PAGE_13_NOT_ENOUGH_LIQUIDITY_TEXT}
          showDialog={notEnoughLiquidity}
          primaryButtonClick={() => setNotEnoughLiquidity(false)}
          secondaryButtonClick={() => {
            setNotEnoughLiquidity(false);
            router.push(`/robot/8?centerId=${centerId}`);
          }}
          primaryButtonText={texts.PAGE_13_CONTINUE}
          secondaryButtonText={texts.PAGE_13_GO_BACK_TO_LIQUIDITY}
        />
        <UserMessageComponent
          userMessage={texts.PAGE_13_NOT_ENOUGH_SAVINGS_TEXT}
          showDialog={notEnoughSavings}
          primaryButtonClick={() => setNotEnoughSavings(false)}
          secondaryButtonClick={() => {
            setNotEnoughSavings(false);
            router.push(`/robot/9?centerId=${centerId}`);
          }}
          primaryButtonText={texts.PAGE_13_CONTINUE}
          secondaryButtonText={texts.PAGE_13_GO_BACK_TO_SAVINGS}
        />
        <UserMessageComponent
          userMessage={texts.PAGE_13_ABOVE_MAX_AMOUNT_TEXT}
          showDialog={aboveMaxAmount}
          secondaryButtonClick={() => {
            setAboveMaxAmount(false);
          }}
          secondaryButtonText={texts.PAGE_13_ABOVE_MAX_AMOUNT_BUTTON_TEXT}
        />
      </MainBox>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={isButtonDisabled}
      />
    </>
  );
};

function IsValidOneTimeDeposit(request: ISavingAgreementData) {
  return request.oneTimeDeposit >= MINIMUM_ONE_TIME_DEPOSIT || request.oneTimeDeposit === 0;
}

function IsValidRequest(isValidMonthlyAmount: boolean, isValidOneTimeDeposit: boolean, request: ISavingAgreementData) {
  return isValidMonthlyAmount && isValidOneTimeDeposit && (request.oneTimeDeposit !== 0 || request.monthlyAmount !== 0);
}
